import { ChevronDown } from "lucide-react";
import { type ReactNode, useId, useState } from "react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";

const STORAGE_PREFIX = "arb-bot.collapsed.";

const readCollapsed = (key: string) => localStorage.getItem(STORAGE_PREFIX + key) === "1";

/** A card whose body folds away; whether it is folded is remembered per `storageKey` in this browser. */
export function CollapsibleCard({
    title,
    hint,
    storageKey,
    contentClassName,
    children,
}: {
    title: string;
    hint?: string;
    storageKey: string;
    contentClassName?: string;
    children: ReactNode;
}) {
    const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey));
    const bodyId = useId();
    const toggle = () => {
        const next = !collapsed;
        if (next) localStorage.setItem(STORAGE_PREFIX + storageKey, "1");
        else localStorage.removeItem(STORAGE_PREFIX + storageKey);
        setCollapsed(next);
    };

    return (
        <Card>
            <CardHeader className={cn("p-0", collapsed && "border-b-0")}>
                <h2 className="w-full">
                    <button
                        type="button"
                        className="flex min-h-11 w-full items-center justify-between gap-2 px-4 py-2 text-left"
                        aria-expanded={!collapsed}
                        aria-controls={bodyId}
                        onClick={toggle}
                    >
                        <span className="font-semibold">{title}</span>
                        <span className="flex items-center gap-2">
                            {hint && <span className="text-xs font-normal text-muted-foreground">{hint}</span>}
                            <ChevronDown
                                className={cn("size-4 shrink-0 transition-transform", collapsed && "-rotate-90")}
                                aria-hidden
                            />
                        </span>
                    </button>
                </h2>
            </CardHeader>
            {!collapsed && (
                <CardContent id={bodyId} className={contentClassName}>
                    {children}
                </CardContent>
            )}
        </Card>
    );
}
